import { getStoreBuilder } from '@codegrade/vuex-typex';
import client from '@/client';
import * as utils from '@/utils';
const storeBuilder = getStoreBuilder();
/** @returns The initial state. */
function makeInitialState() {
    return {
        byCourse: {},
    };
}
const moduleBuilder = storeBuilder.module('group-sets', makeInitialState());
/** The store for the groupSets. */
export var GroupSetsStore;
(function (GroupSetsStore) {
    /** This mutation will clear all the data in the group set store. */
    GroupSetsStore.commitClearAll = moduleBuilder.commit(state => Object.assign(state, makeInitialState()), 'commitClearAll');
    /** Commit the updated data into the store for a given course. */
    GroupSetsStore.commitUpdateCourseGroupSets = moduleBuilder.commit((state, { courseId, updater, }) => {
        const existing = state.byCourse[courseId];
        if (existing == null) {
            return;
        }
        utils.vueSet(state.byCourse, courseId, {
            ...existing,
            data: existing.data?.map(g => updater(g)),
        });
    }, 'commitGroupSet');
    /** Commit the group sets to the store for a given course. */
    const commitGroupSets = moduleBuilder.commit((state, { courseId, data, }) => {
        utils.vueSet(state.byCourse, courseId, data);
    }, 'commitGroupSets');
    /** Getter to get group sets for a given course from the store. */
    GroupSetsStore.getForCourse = moduleBuilder.read(
    /**
     * This function returns the actual getter.
     *
     * @param state The current store data.
     * @returns Getter to get group sets for a given course from the store.
     */
    state => {
        /**
         * Getter to get group sets for a given course from the store.
         *
         * @param courseId Course id.
         * @returns A PaginatedState object of the group set in the course.
         */
        function getter(courseId) {
            return utils.getPaginatedState(state.byCourse[courseId]);
        }
        return getter;
    }, 'getForCourse');
    /** Action to load group sets for a course. */
    GroupSetsStore.loadForCourse = moduleBuilder.dispatch(async ({ state }, { courseId, toLoad, }) => {
        await utils.loadPaginatedWithForce({
            commitData: data => commitGroupSets({ courseId, data }),
            getData: () => state.byCourse[courseId],
            load: async (nextToken) => await client.p.course.getGroupSets({ courseId, nextToken }),
            toLoad,
        });
    }, 'loadForCourse');
    /** Action to create a group set. */
    GroupSetsStore.createGroupSet = moduleBuilder.dispatch(async ({ state }, { courseId, }) => {
        const res = await client.p.course.createGroupSet({
            courseId,
            requestBody: {
                minimumSize: 1,
                maximumSize: 1,
            },
        });
        const data = state.byCourse[courseId] ?? {
            pagination: { tag: 'not-started' },
        };
        commitGroupSets({
            courseId,
            data: {
                ...data,
                data: [res.cgResult, ...(data.data ?? [])],
            },
        });
        return res;
    }, 'createGroupSet');
    /** Action to patch a group set. */
    GroupSetsStore.patchGroupSet = moduleBuilder.dispatch(async (_, { courseId, data, }) => {
        const res = await client.p.course.createGroupSet({
            courseId,
            requestBody: data,
        });
        return res.addCallbacks({
            /** Commit the update to the store after the call. */
            onSuccess() {
                GroupSetsStore.commitUpdateCourseGroupSets({
                    courseId,
                    updater: old => (old.id === data.id ? res.cgResult : old),
                });
            },
        });
    }, 'patchGroupSet');
    /** Action to delete a group set. */
    GroupSetsStore.deleteGroupSet = moduleBuilder.dispatch(async ({ state }, { courseId, groupSet, }) => {
        const res = await client.p.groupSet.delete({
            groupSetId: groupSet.id,
        });
        return res.addCallbacks({
            /** Commit group set after the call. */
            onAfterSuccess() {
                const data = state.byCourse[courseId];
                if (data == null) {
                    return;
                }
                commitGroupSets({
                    courseId,
                    data: {
                        ...data,
                        data: data.data?.filter(gs => gs.id !== groupSet.id),
                    },
                });
            },
        });
    }, 'deleteGroupSet');
    utils.signal.AFTER_USER_CHANGED.register(async () => {
        GroupSetsStore.commitClearAll();
    });
})(GroupSetsStore || (GroupSetsStore = {}));
